import { useAlternative, type AlternativeProject } from "../hooks/useAlternatives.ts";
import { useDocumentMeta } from "../hooks/useDocumentMeta.ts";
import { Spinner } from "../components/Spinner.tsx";
import { navigate } from "../lib/router.ts";
import { formatNumber, formatDate } from "../lib/format.ts";

function AlternativeCard({ project, rank }: { project: AlternativeProject; rank: number }) {
  return (
    <a
      href={project.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-4 px-4 py-3 border-b border-border hover:bg-surface-hover transition-colors"
    >
      <span className="text-text-muted text-xs w-6 text-right shrink-0 tabular-nums">
        {rank}
      </span>

      {project.logo_url && (
        <img
          src={project.logo_url}
          alt=""
          className="w-6 h-6 rounded shrink-0 grayscale group-hover:grayscale-0 transition-all"
          loading="lazy"
        />
      )}

      <div className="min-w-0 flex-1 flex items-center gap-3">
        <span className="text-sm text-text-primary group-hover:text-accent transition-colors truncate font-medium">
          {project.full_name}
        </span>
        {project.description && (
          <span className="text-xs text-text-muted truncate hidden lg:inline">
            {project.description}
          </span>
        )}
      </div>

      <div className="flex items-center gap-4 shrink-0 text-xs text-text-secondary tabular-nums">
        <span className="hidden sm:inline text-text-muted w-16">{project.language || ""}</span>
        <span className="w-16 text-right" title="Stars">
          {formatNumber(project.stars)}
        </span>
        <span className="w-14 text-right hidden sm:inline" title="Commits in last 3 months">
          {project.commits_3mo > 0 ? formatNumber(project.commits_3mo) : "—"}
        </span>
        {project.last_commit_at ? (
          <span className="w-20 text-right text-text-muted hidden md:inline" title="Last commit">
            {formatDate(project.last_commit_at)}
          </span>
        ) : project.updated_at_gh ? (
          <span className="w-20 text-right text-text-muted hidden md:inline" title="Last updated">
            {formatDate(project.updated_at_gh)}
          </span>
        ) : null}
      </div>
    </a>
  );
}

export function AlternativesPage({ slug }: { slug: string }) {
  const { tool, alternatives, loading, error } = useAlternative(slug);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Spinner />
      </div>
    );
  }

  if (error || !tool) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate("/"); }}
          className="text-xs text-text-muted hover:text-accent transition-colors mb-4 inline-block"
        >
          &larr; back
        </a>
        <p className="text-sm text-text-muted">Tool not found.</p>
      </div>
    );
  }

  const topAlt = alternatives[0];
  const summaryText =
    alternatives.length > 0 && topAlt
      ? `There are ${alternatives.length} open-source alternatives to ${tool.name}. The most popular is ${topAlt.name} with ${topAlt.stars.toLocaleString()} GitHub stars.`
      : `Find open-source alternatives to ${tool.name}.`;

  useDocumentMeta({
    title: `Open-Source Alternatives to ${tool.name} | There's an OS for That`,
    description: summaryText,
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <a
        href="/"
        onClick={(e) => { e.preventDefault(); navigate("/"); }}
        className="text-xs text-text-muted hover:text-accent transition-colors mb-4 inline-block"
      >
        &larr; back
      </a>

      {/* Tool Header */}
      <div className="mb-8">
        <h1 className="text-sm text-text-primary mb-1">
          Open-Source Alternatives to <span className="text-accent">{tool.name}</span>
        </h1>
        <p className="text-xs text-text-secondary mb-2">{summaryText}</p>
        {tool.description && (
          <p className="text-xs text-text-muted mb-2">{tool.description}</p>
        )}
        {tool.website && (
          <a
            href={tool.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-text-muted hover:text-accent transition-colors"
          >
            {tool.website.replace(/^https?:\/\//, "")} &rarr;
          </a>
        )}
      </div>

      {/* Alternatives Table */}
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-[10px] uppercase tracking-widest text-text-muted">
          {alternatives.length} alternative{alternatives.length !== 1 ? "s" : ""} found
        </h2>
        <div className="flex-1 h-px bg-border" />
      </div>

      {alternatives.length === 0 ? (
        <div className="text-center py-16 text-text-muted text-xs">
          no alternatives found yet — run the alternatives scraper to populate data
        </div>
      ) : (
        <div className="border-t border-border">
          {/* Table header */}
          <div className="flex items-center gap-4 px-4 py-2 text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
            <span className="w-6 text-right shrink-0">#</span>
            <span className="w-6 shrink-0"></span>
            <span className="flex-1">Project</span>
            <div className="flex items-center gap-4 shrink-0">
              <span className="hidden sm:inline w-16">Lang</span>
              <span className="w-16 text-right">Stars</span>
              <span className="w-14 text-right hidden sm:inline">3mo</span>
              <span className="w-20 text-right hidden md:inline">Last Commit</span>
            </div>
          </div>
          {alternatives.map((p, i) => (
            <AlternativeCard key={p.id} project={p} rank={i + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
